/**
 * Photo tab — right panel for the visual editor's photo mode.
 * Phase 2: gallery grid + lightbox viewer + upload-from-tab.
 *
 * No backend changes — uses the existing single-photo-per-defect model
 * and the same `uploadDefectPhoto` callback from the parent page.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Camera, ChevronLeft, ChevronRight, Upload, X } from "lucide-react";
import {
  Button,
  DialogContent,
  DialogRoot,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusPill,
} from "@flint/ui";
import { SEVERITY, type DefectPin } from "./defect-tab.tsx";
import { Placeholder } from "./canvas-helpers.tsx";

/* ── PhotoPanel ──────────────────────────────────────────────────────── */

export function PhotoPanel({
  pins,
  allDefects,
  selectedPinId,
  onSelectPin,
  uploading,
  onUploadPhoto,
}: {
  /** Defects WITH photos — gallery items. */
  pins: DefectPin[];
  /** ALL defects — for upload-target dropdown. */
  allDefects: DefectPin[];
  /** Bidirectional canvas <-> gallery highlight. */
  selectedPinId: string | null;
  onSelectPin: (id: string | null) => void;
  /** True while any upload is in progress. */
  uploading: boolean;
  /** Upload a photo for a specific defect. */
  onUploadPhoto: (defectId: string, file: File) => void;
}) {
  const [lightboxId, setLightboxId] = useState<string | null>(null);

  // Defects without a photo — candidates for the upload dropdown
  const unphotoedDefects = allDefects.filter((d) => !d.photoKey);

  // Lightbox navigation index
  const lightboxIndex = pins.findIndex((p) => p.id === lightboxId);
  const lightboxPin = (lightboxIndex >= 0 ? pins[lightboxIndex] : null) ?? null;

  const goPrev = useCallback(() => {
    if (pins.length === 0) return;
    const i = lightboxIndex <= 0 ? pins.length - 1 : lightboxIndex - 1;
    setLightboxId(pins[i]!.id);
  }, [pins, lightboxIndex]);

  const goNext = useCallback(() => {
    if (pins.length === 0) return;
    const i = lightboxIndex >= pins.length - 1 ? 0 : lightboxIndex + 1;
    setLightboxId(pins[i]!.id);
  }, [pins, lightboxIndex]);

  // Keyboard nav inside lightbox
  useEffect(() => {
    if (!lightboxPin) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        goNext();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightboxPin, goPrev, goNext]);

  // Empty state — no photos and no un-photo'd defects to upload to
  if (pins.length === 0 && unphotoedDefects.length === 0) {
    return (
      <Placeholder
        title="Photos"
        body="Attach a defect photo from the Defect tab — select a pin, then \u201CAttach photo\u201D. They gather here."
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-sm font-semibold text-ink">Photos</h3>

      {/* ── Upload section ──────────────────────────────────────────── */}
      {unphotoedDefects.length > 0 ? (
        <UploadSection
          defects={unphotoedDefects}
          uploading={uploading}
          onUploadPhoto={onUploadPhoto}
        />
      ) : null}

      {/* ── Gallery grid ────────────────────────────────────────────── */}
      {pins.length > 0 ? (
        <ul className="grid grid-cols-2 gap-2">
          {pins.map((p) => (
            <PhotoCard
              key={p.id}
              pin={p}
              selected={p.id === selectedPinId}
              onSelect={() => onSelectPin(p.id)}
              onOpen={() => setLightboxId(p.id)}
            />
          ))}
        </ul>
      ) : (
        <div className="rounded-sm border border-dashed border-line bg-bg px-3 py-4 text-center text-xs text-muted">
          No photos yet — pick a defect above and upload one.
        </div>
      )}

      {/* ── Lightbox ────────────────────────────────────────────────── */}
      <Lightbox
        pin={lightboxPin}
        index={lightboxIndex}
        total={pins.length}
        onPrev={goPrev}
        onNext={goNext}
        onClose={() => setLightboxId(null)}
        uploading={uploading}
        onReplace={(file) => {
          if (lightboxPin) onUploadPhoto(lightboxPin.id, file);
        }}
      />
    </div>
  );
}

/* ── Upload Section ──────────────────────────────────────────────────── */

function UploadSection({
  defects,
  uploading,
  onUploadPhoto,
}: {
  defects: DefectPin[];
  uploading: boolean;
  onUploadPhoto: (defectId: string, file: File) => void;
}) {
  const [targetId, setTargetId] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="flex flex-col gap-2 rounded-sm border border-dashed border-line bg-bg p-2.5">
      <p className="text-xs font-medium text-ink-2">Upload a defect photo</p>
      <Select value={targetId} onValueChange={setTargetId}>
        <SelectTrigger className="h-8 text-xs">
          <SelectValue placeholder="Select a defect\u2026" />
        </SelectTrigger>
        <SelectContent>
          {defects.map((d) => {
            const sev = SEVERITY[d.severity];
            return (
              <SelectItem key={d.id} value={d.id}>
                <span className="flex items-center gap-1.5">
                  <span
                    className={`inline-block h-2 w-2 shrink-0 rounded-full ${sev.dot}`}
                  />
                  {d.label} &middot; {d.defectType}
                </span>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          const f = e.currentTarget.files?.[0];
          if (f && targetId) {
            onUploadPhoto(targetId, f);
            setTargetId("");
          }
          e.currentTarget.value = "";
        }}
      />
      <Button
        size="sm"
        intent="ghost"
        className="border border-line"
        disabled={!targetId || uploading}
        onClick={() => inputRef.current?.click()}
      >
        <Upload size={14} />
        {uploading ? "Uploading\u2026" : "Upload"}
      </Button>
    </div>
  );
}

/* ── Photo Card ──────────────────────────────────────────────────────── */

function PhotoCard({
  pin,
  selected,
  onSelect,
  onOpen,
}: {
  pin: DefectPin;
  selected: boolean;
  onSelect: () => void;
  onOpen: () => void;
}) {
  const sev = SEVERITY[pin.severity];

  return (
    <li>
      <button
        type="button"
        onClick={() => {
          onSelect();
          onOpen();
        }}
        title={`${pin.label} \u00b7 ${pin.defectType}`}
        className={`group relative block w-full overflow-hidden rounded-sm border transition ${
          selected
            ? "border-sky-400 ring-2 ring-sky-200"
            : "border-line hover:border-ink-2/30"
        }`}
      >
        {pin.photoUrl ? (
          <img
            src={pin.photoUrl}
            alt={pin.defectType}
            className="aspect-square w-full object-cover"
          />
        ) : (
          <span className="grid aspect-square place-items-center text-xs text-muted">
            {pin.label}
          </span>
        )}
        {/* Bottom overlay */}
        <span className="absolute inset-x-0 bottom-0 flex items-center gap-1.5 bg-ink/60 px-2 py-1 text-left text-xs leading-tight text-paper backdrop-blur-sm">
          <span
            className={`inline-block h-2 w-2 shrink-0 rounded-full ${sev.dot}`}
          />
          <span className="min-w-0 truncate">
            {pin.label} &middot; {pin.defectType}
          </span>
        </span>
      </button>
    </li>
  );
}

/* ── Lightbox ────────────────────────────────────────────────────────── */

function Lightbox({
  pin,
  index,
  total,
  onPrev,
  onNext,
  onClose,
  uploading,
  onReplace,
}: {
  pin: DefectPin | null;
  index: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
  uploading: boolean;
  onReplace: (file: File) => void;
}) {
  const replaceRef = useRef<HTMLInputElement>(null);

  if (!pin) return null;

  const sev = SEVERITY[pin.severity];

  return (
    <DialogRoot open={pin !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl p-0" hideClose>
        {/* Image area */}
        <div className="relative flex items-center justify-center bg-ink/5 px-10 py-6">
          {pin.photoUrl ? (
            <img
              src={pin.photoUrl}
              alt={`${pin.label} \u2014 ${pin.defectType}`}
              className="max-h-[70vh] rounded-sm object-contain"
            />
          ) : (
            <div className="grid h-48 w-full place-items-center text-sm text-muted">
              No photo
            </div>
          )}

          {/* Prev / Next nav (only if more than 1 photo) */}
          {total > 1 ? (
            <>
              <button
                type="button"
                onClick={onPrev}
                aria-label="Previous photo"
                className="absolute left-2 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full bg-paper/80 text-ink shadow backdrop-blur-sm transition hover:bg-paper"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                type="button"
                onClick={onNext}
                aria-label="Next photo"
                className="absolute right-2 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full bg-paper/80 text-ink shadow backdrop-blur-sm transition hover:bg-paper"
              >
                <ChevronRight size={16} />
              </button>
            </>
          ) : null}
        </div>

        {/* Info bar */}
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line px-4 py-3">
          <div className="flex items-center gap-2">
            <StatusPill tone={sev.tone} size="sm" dot>
              {sev.label}
            </StatusPill>
            <span className="text-sm font-semibold text-ink">
              {pin.label} &middot; {pin.defectType}
            </span>
          </div>
          <div className="flex items-center gap-3 text-xs text-muted">
            {pin.zone ? <span>{pin.zone}</span> : null}
            <span className="font-mono">{pin.lotRef}</span>
            <span>
              {index + 1} of {total}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-line px-4 py-2.5">
          <div>
            <input
              ref={replaceRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => {
                const f = e.currentTarget.files?.[0];
                if (f) onReplace(f);
                e.currentTarget.value = "";
              }}
            />
            <Button
              size="sm"
              intent="ghost"
              className="border border-line"
              disabled={uploading}
              onClick={() => replaceRef.current?.click()}
            >
              <Camera size={14} />
              {uploading ? "Uploading\u2026" : "Replace photo"}
            </Button>
          </div>
          <Button size="sm" intent="ghost" onClick={onClose}>
            <X size={14} />
            Close
          </Button>
        </div>
      </DialogContent>
    </DialogRoot>
  );
}
